import { inject, injectable } from "inversify";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import type { IOrganizationStore } from "../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../services/IOrganizationStore";
import { computed, makeObservable, observable, reaction, runInAction } from "mobx";
import i18n from "../../i18n/config";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { hasAnyPermission } from "@whatslly/commons/platformPermissions";
import { getJSON } from "@whatslly/commons/apiUtils";
import { parseOrReturnError } from "../../services/utils";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import { OrgConfigs } from "@whatslly/types/OrgConfigs";
import type { CreateSidecar } from "@whatslly/types/sidecars-server/Sidecar";
import { channelTypeOptions } from "@whatslly/types/sidecars-server/Sidecar";
import { platformTypeOptions } from "@whatslly/types/tuvis-messaging/TuvisMessagingPlatforms";
import type { SupportedSidecarChannel } from "@whatslly/commons/sidecarUtils";
import { remoteSyncChannelsConfig } from "./remoteSyncChannelsConfig";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { OrgConfigsStore } from "../../services/OrgConfigsStore";
import type { IconType } from "react-icons";
const t = i18n.t.bind(i18n);

export type DesktopChannel = "whatsapp" | "telegram" | "imessage";
export type CommonChannel = "waba" | "twilio";
type ChannelsStoreConfig = Pick<OrgConfigs, (typeof keys)[number]>;

type ChannelsOverviewItem = {
  name: string;
  category: string;
  permission: boolean;
  connected: boolean;
  navigateTo?: string;
  icon?: IconType;
  img?: string;
  imgType?: "svg" | "png";
  showRevokeButton?: boolean;
  revokeFunction?: () => void | Promise<void>;
  disabled?: boolean;
  disabledTooltip?: string;
};

const keys = [
  "hide_whatsapp_channel",
  "hide_telegram_channel",
  "allow_imessage",
  "allow_telegram",
  "max_sidecars",
  ...channelTypeOptions.map((c) => c.orgConfigKey),
  ...channelTypeOptions.map((c) => c.maxDevicesConfigKey),
  ...platformTypeOptions.map((c) => c.orgConfigKey),
] as const;
const defaultConfigs: ChannelsStoreConfig = {
  ...Object.fromEntries(keys.map((key) => [key, false])),
  max_sidecars: 0,
  max_devices_whatsapp: 0,
  max_devices_telegram: 0,
  max_devices_line: 0,
  max_devices_signal: 0,
};

@injectable()
export class ChannelsPageStore {
  public initialized = false;
  public configs = defaultConfigs;
  private sidecarChannels = channelTypeOptions.reduce<PartialRecord<Exclude<CreateSidecar["channelType"], undefined>, boolean>>((acc, c) => {
    acc[c.value] = false;
    return acc;
  }, {});
  private sidecarsCount = 0;
  private forceRefresh = 0;
  private initPromise: IPromiseBasedObservable<void> | null = null;
  private lastForceRefresh = 0;
  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(OrgConfigsStore) private readonly orgConfigsStore: OrgConfigsStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable<ChannelsPageStore, "forceRefresh" | "sidecarChannels" | "lastForceRefresh" | "configs" | "sidecarsCount">(this, {
      channels: computed,
      init: computed,
      forceRefresh: observable,
      lastForceRefresh: observable,
      sidecarChannels: observable,
      configs: observable,
      sidecarsCount: observable,
      totalSidecars: computed,
    });

    reaction(
      () => this.orgStore.orgId,
      () => {
        this.forceRefresh++;
      },
      { fireImmediately: true }
    );
  }

  get init() {
    if (this.forceRefresh !== this.lastForceRefresh) {
      this.initPromise = null;
      this.lastForceRefresh = this.forceRefresh;
    }

    const { state, value } = this.orgStore.orgId;
    if ((state === "fulfilled" && value === "") || state === "pending") {
      return fromPromise(Promise.resolve([]));
    }

    if (!this.initPromise) {
      const initializationPromise = Promise.all([
        this.getConfig(),
        this.loadSidecarsCount().catch(() => {
          //ignore
        }),
      ]);

      this.initPromise = fromPromise(
        initializationPromise.then(() =>
          runInAction(() => {
            this.initialized = true;
          })
        )
      );
    }

    return this.initPromise;
  }

  private async getConfig() {
    const res = await this.orgConfigsStore.getObservableConfigs(keys);
    runInAction(() => {
      channelTypeOptions.forEach((c) => {
        this.sidecarChannels[c.value] = Boolean(res?.[c.orgConfigKey]);
      });
      this.configs = res ?? defaultConfigs;
    });
  }

  async loadSidecarsCount() {
    if (!hasAnyPermission(this.userStore.user, [UserPermissions["sidecars.view"]])) {
      return;
    }
    const orgId = await this.orgStore.orgId;
    if (!orgId) return;
    const res = await getJSON({
      urlSuffix: `/sidecars/count?orgId=${orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: import.meta.env.VITE_API_DEFAULT_POD,
    }).then((r) => parseOrReturnError<{ success: true; count: number }>(r, this.logger));
    this.totalSidecars = res.count;
  }

  get totalSidecars() {
    return this.sidecarsCount;
  }

  set totalSidecars(count: number) {
    this.sidecarsCount = count;
  }

  get channels(): ChannelsOverviewItem[] {
    const remoteSyncCards = remoteSyncChannelsConfig.map((channel) => ({
      name: channel.displayName,
      icon: channel.icon,
      navigateTo: channel.route,
      permission: Boolean(hasAnyPermission(this.userStore.user, [UserPermissions["sidecars.view"]])),
      connected: this.isSidecarChannelAvailable(channel.channelKey),
      disabled: !this.isSidecarChannelAvailable(channel.channelKey),
      disabledTooltip: this.getRemoteSyncUnavailableTooltip(channel.displayName),
      category: "tuvis-mobile",
    }));

    remoteSyncCards.sort((a, b) => Number(Boolean(a.disabled)) - Number(Boolean(b.disabled)));

    return [...remoteSyncCards];
  }

  getSidecarChannelQuota(channel: SupportedSidecarChannel): number {
    const channelOption = channelTypeOptions.find((opt) => opt.value === channel);
    if (!channelOption) return 0;
    const quota = this.configs[channelOption.maxDevicesConfigKey];
    // For WhatsApp, fall back to legacy max_sidecars if max_devices_whatsapp is not set (0 is the default)
    // Todo(alon): Remove this once we have a proper way to set the max devices for WhatsApp
    if (channel === "whatsapp" && quota === 0) {
      return this.configs.max_sidecars ?? 0;
    }
    return typeof quota === "number" ? quota : 0;
  }

  isSidecarChannelEnabledInOrg(channel: SupportedSidecarChannel): boolean {
    return Boolean(this.sidecarChannels[channel]);
  }

  isSidecarChannelAvailable(channel: SupportedSidecarChannel): boolean {
    return this.isSidecarChannelEnabledInOrg(channel) && this.getSidecarChannelQuota(channel) > 0;
  }

  getRemoteSyncUnavailableTooltip(channelDisplayName: string): string {
    return `${t("channels.unavailable.title")}\n${t("channels.unavailable.message", { channel: channelDisplayName })}`;
  }

  isSidecarChannelEnabled(channel: SupportedSidecarChannel) {
    return (
      this.isSidecarChannelEnabledInOrg(channel) &&
      hasAnyPermission(this.userStore.user, [UserPermissions["sidecars.view"], UserPermissions["sidecars.update"]])
    );
  }
}
